<template>
  <div class="tara-attrition-card">

    <!-- Header -->
    <div class="tac-header">
      <div class="tac-header-left">
        <i class="ti ti-sparkles" aria-hidden="true"></i>
        <span>Attrition risk dashboard</span>
      </div>
      <span class="tac-sub">Live data · {{ new Date().toLocaleDateString('en-MY') }}</span>
    </div>

    <!-- Metric tiles -->
    <div class="tac-metrics">
      <div class="tac-metric">
        <div class="tac-metric-val" :style="{ color: data.high.length > 0 ? '#A32D2D' : 'inherit' }">
          {{ data.high.length }}
        </div>
        <div class="tac-metric-label">High risk</div>
      </div>
      <div class="tac-metric">
        <div class="tac-metric-val" :style="{ color: data.medium.length > 0 ? '#854F0B' : 'inherit' }">
          {{ data.medium.length }}
        </div>
        <div class="tac-metric-label">Medium risk</div>
      </div>
      <div class="tac-metric">
        <div class="tac-metric-val" style="color:#3B6D11">{{ data.low.length }}</div>
        <div class="tac-metric-label">Low risk</div>
      </div>
      <div class="tac-metric">
        <div class="tac-metric-val">{{ data.total }}</div>
        <div class="tac-metric-label">Total flagged</div>
      </div>
    </div>

    <!-- Staff list -->
    <div class="tac-list">
      <div
        v-for="(staff, i) in topStaff"
        :key="i"
        class="tac-row"
      >
        <!-- Avatar -->
        <div class="tac-avatar" :style="avatarStyle(staff)">
          {{ initials(staff.psnname) }}
        </div>
        <!-- Info -->
        <div class="tac-info">
          <div class="tac-name">{{ staff.psnname }}</div>
          <div class="tac-dept">{{ staff.deptname }} · {{ staff.tenure_years }} yrs</div>
        </div>
        <!-- Score bar -->
        <div class="tac-score-wrap">
          <div class="tac-bar-bg">
            <div
              class="tac-bar-fill"
              :style="{ width: staff.risk_score + '%', background: scoreColor(staff.risk_score) }"
            ></div>
          </div>
          <div class="tac-score-num" :style="{ color: scoreColor(staff.risk_score) }">
            {{ staff.risk_score }}
          </div>
        </div>
        <!-- Badge -->
        <span class="tac-badge" :class="badgeClass(staff.risk_level)">
          {{ staff.risk_level }}
        </span>
      </div>
    </div>

    <!-- Footer actions -->
    <div class="tac-footer">
      <button class="tac-btn" @click="askMore('Show me the top risk signals for each employee')">
        See signals ↗
      </button>
      <button class="tac-btn" @click="askMore('What actions should I take to retain my high risk staff?')">
        Retention plan ↗
      </button>
    </div>

  </div>
</template>

<script>
export default {
  name: "TaraAttritionCard",
  props: {
    data: {
      type: Object,
      required: true,
    },
  },
  computed: {
    topStaff() {
      return (this.data.all || []).slice(0, 8);
    },
  },
  methods: {
    initials(name) {
      if (!name) return "?";
      const parts = name.trim().split(" ");
      return parts.length >= 2
        ? (parts[0][0] + parts[1][0]).toUpperCase()
        : name.substring(0, 2).toUpperCase();
    },
    avatarStyle(staff) {
      const level = staff.risk_level;
      if (level === "HIGH")   return { background: "#FCEBEB", color: "#791F1F" };
      if (level === "MEDIUM") return { background: "#FAEEDA", color: "#633806" };
      return { background: "#EAF3DE", color: "#27500A" };
    },
    scoreColor(score) {
      if (score >= 70) return "#A32D2D";
      if (score >= 40) return "#854F0B";
      return "#3B6D11";
    },
    badgeClass(level) {
      if (level === "HIGH")   return "tac-badge-high";
      if (level === "MEDIUM") return "tac-badge-med";
      return "tac-badge-low";
    },
    askMore(text) {
      this.$emit("ask", text);
    },
  },
};
</script>

<style scoped>
.tara-attrition-card {
  border: 0.5px solid #AFA9EC;
  border-radius: 12px;
  background: #FAFAFE;
  overflow: hidden;
  font-family: inherit;
  font-size: 13px;
  margin: 4px 0 8px;
  max-width: 420px;
}
.tac-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px 8px;
  background: #EEEDFE;
  border-bottom: 0.5px solid #AFA9EC;
}
.tac-header-left {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
  color: #3C3489;
  font-size: 13px;
}
.tac-header-left i { font-size: 15px; }
.tac-sub { font-size: 11px; color: #534AB7; }
.tac-metrics {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0;
  border-bottom: 0.5px solid #E5E3F8;
}
.tac-metric {
  padding: 10px 8px;
  text-align: center;
  border-right: 0.5px solid #E5E3F8;
}
.tac-metric:last-child { border-right: none; }
.tac-metric-val {
  font-size: 20px;
  font-weight: 500;
  color: #26215C;
  line-height: 1.2;
}
.tac-metric-label {
  font-size: 10px;
  color: #7F77DD;
  margin-top: 2px;
}
.tac-list { padding: 6px 0; }
.tac-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 14px;
  border-bottom: 0.5px solid #F0EFF9;
}
.tac-row:last-child { border-bottom: none; }
.tac-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 500;
  flex-shrink: 0;
}
.tac-info { flex: 1; min-width: 0; }
.tac-name {
  font-size: 12px;
  font-weight: 500;
  color: #26215C;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.tac-dept { font-size: 10px; color: #7F77DD; margin-top: 1px; }
.tac-score-wrap {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 3px;
  width: 56px;
  flex-shrink: 0;
}
.tac-bar-bg {
  width: 100%;
  height: 4px;
  background: #E5E3F8;
  border-radius: 2px;
  overflow: hidden;
}
.tac-bar-fill { height: 100%; border-radius: 2px; }
.tac-score-num { font-size: 10px; font-weight: 500; }
.tac-badge {
  font-size: 10px;
  font-weight: 500;
  padding: 2px 6px;
  border-radius: 20px;
  flex-shrink: 0;
}
.tac-badge-high   { background: #FCEBEB; color: #791F1F; }
.tac-badge-med    { background: #FAEEDA; color: #633806; }
.tac-badge-low    { background: #EAF3DE; color: #27500A; }
.tac-footer {
  display: flex;
  gap: 8px;
  padding: 10px 14px;
  background: #F5F4FD;
  border-top: 0.5px solid #E5E3F8;
}
.tac-btn {
  font-size: 12px;
  padding: 5px 10px;
  border-radius: 6px;
  border: 0.5px solid #AFA9EC;
  background: white;
  color: #3C3489;
  cursor: pointer;
}
.tac-btn:hover { background: #EEEDFE; }
</style>
